import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
import { employmentTypeKeys } from "@/lib/query";
import type {
  EmploymentTypeCreateInput,
  EmploymentTypeUpdateInput,
} from "@/schemas/employment-types";

export interface EmploymentType {
  id: string;
  /** Code-stable token stored on `person.employment_type`. Never editable. */
  key: string;
  /** Admin-editable display label. */
  name: string;
  /** NULL = this type carries no weekly expectation at all. */
  expectedWeeklyHours: number | null;
  sortOrder: number;
  isActive: boolean;
  /** How many people currently hold this type — a type in use can't be deleted. */
  personCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Every employment type, active and inactive, in admin order. Inactive ones are
 * included so a person who still holds a retired type renders with its real
 * label rather than a raw token — pickers filter with `useEmploymentTypeOptions`.
 */
export function useEmploymentTypes(enabled = true) {
  return useQuery({
    queryKey: employmentTypeKeys.all,
    queryFn: async (): Promise<EmploymentType[]> => {
      const res = await fetch("/api/employment-types");
      if (!res.ok) throw new Error("Failed to fetch employment types");
      const json = await res.json();
      return json.data;
    },
    enabled,
  });
}

/**
 * Picker options (active types only) plus a token → label map that covers
 * retired types too. Every employment-type `<Select>`, filter and badge reads
 * from here, so there is no second copy of the list anywhere in the client.
 */
export function useEmploymentTypeOptions() {
  const query = useEmploymentTypes();
  const types = useMemo(() => query.data ?? [], [query.data]);
  return useMemo(
    () => ({
      isLoading: query.isLoading,
      options: types.filter((t) => t.isActive).map((t) => ({ value: t.key, label: t.name })),
      labelFor: (key: string) => types.find((t) => t.key === key)?.name ?? key,
    }),
    [types, query.isLoading],
  );
}

async function mutate(url: string, method: string, body?: unknown) {
  const res = await fetch(url, {
    method,
    ...(body !== undefined && {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  });
  if (!res.ok) {
    // The delete and create refusals carry a specific message (a type in use, a
    // built-in type, a duplicate key); surfacing it is the whole point.
    const json = await res.json().catch(() => null);
    throw new Error(json?.error ?? "Request failed");
  }
  return res.status === 204 ? null : res.json().catch(() => null);
}

export function useCreateEmploymentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmploymentTypeCreateInput) =>
      mutate("/api/employment-types", "POST", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employmentTypeKeys.all });
      toast.success("Employment type created");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdateEmploymentType(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmploymentTypeUpdateInput) =>
      mutate(`/api/employment-types/${id}`, "PUT", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employmentTypeKeys.all });
      toast.success("Employment type updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useReorderEmploymentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: "up" | "down" }) =>
      mutate(`/api/employment-types/${id}/reorder`, "PATCH", { direction }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employmentTypeKeys.all });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDeleteEmploymentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mutate(`/api/employment-types/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employmentTypeKeys.all });
      toast.success("Employment type deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
